import type { GitProvider } from '../api';

export interface SavedGitCredential {
  account: string;
  token: string;
  host?: string;
  connectedAt: string;
}

/**
 * Git provider credentials entered in the dashboard live in memory for the
 * current page load only — never in localStorage or sessionStorage. A
 * personal access token is a secret: the place it is kept is the project's
 * Git connection on the server, encrypted at rest, which the project wizard
 * writes when the project is created. What this module holds is just the
 * hand-over between the Integrations page and the wizard within one visit.
 *
 * (An earlier version persisted tokens in localStorage in plaintext and
 * showed the provider as "Connected" for the whole organization although
 * nothing was stored server-side — see docs/UX_UI_AUDIT.md.)
 */
const memory = new Map<GitProvider, SavedGitCredential>();

export function getSavedGitCredential(provider: GitProvider): SavedGitCredential | null {
  return memory.get(provider) ?? null;
}

export function saveGitCredential(provider: GitProvider, cred: SavedGitCredential): void {
  memory.set(provider, cred);
  try {
    window.dispatchEvent(new CustomEvent('co_git_credentials_updated', { detail: { provider } }));
  } catch {
    // not in a browser
  }
}

export function removeGitCredential(provider: GitProvider): void {
  memory.delete(provider);
  try {
    window.dispatchEvent(new CustomEvent('co_git_credentials_updated', { detail: { provider } }));
  } catch {
    // not in a browser
  }
}

/** clearSavedGitCredentials forgets every in-memory credential (tests, sign-out). */
export function clearSavedGitCredentials(): void {
  memory.clear();
}
